import React from "react";
import {
	ArrayPick,
	ChildrenProps,
	ClassNameProps,
	Column,
	Heading,
	If,
	makeClassName,
	Modal,
	Row,
	Section,
	StyleProps,
	Timestamp,
} from "@hex-insights/core";
import { NewBadge } from "../../../../Components/Badge";
import { Announcement, useAnnouncementSocialItemsQuery, User, UserNameAndImageFragment } from "../../../../Utilities";
import { AnnouncementFormat, UserFormat } from "../../../Format";
import { UserLink } from "../../../Links";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { SharingView } from "../../SharingPolicy";
import * as SocialReactionComponents from "../../SocialReaction";
import * as UserComponents from "../../User";
import { AnnouncementUpdatePermissionRequired } from "../PermissionRequired";
import { AnnouncementStatusBadge } from "../StatusBadge";
import { ControlledAnnouncementUpdateModalController } from "../UpdateModal";
import styles from "./styles.module.css";

export type DetailDisplayAnnouncement = Pick<Announcement, "id" | "title" | "body" | "startTime" | "endTime"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	mediaAttachments: ArrayPick<
		Announcement["mediaAttachments"],
		"id" | "title" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	>;
};

export type AnnouncementDetailDisplayProps = {
	announcement: DetailDisplayAnnouncement;
	isNew?: boolean;
	withEndTime?: boolean;
	withStatus?: boolean;
	withAudience?: boolean;
	editable?: boolean;
	withReactions?: boolean;
	isPreview?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function AnnouncementDetailDisplay({
	announcement,
	isNew,
	withEndTime = false,
	withStatus = false,
	withAudience = false,
	editable = false,
	withReactions = false,
	isPreview = false,
	className,
	style,
}: AnnouncementDetailDisplayProps) {
	const { data: socialData } = useAnnouncementSocialItemsQuery({
		variables: { id: announcement.id },
		skip: !withReactions,
	});

	return (
		<Column className={className} style={style}>
			<AuthorTimestampView announcement={announcement} isNew={isNew} style={{ marginBottom: "2.25rem" }}>
				<If condition={withEndTime}>
					<span
						className={makeClassName(
							styles["detail-display__end-time"],
							!announcement.endTime ? styles["detail-display__end-time--no-end-time"] : "",
						)}
					>
						Expires:{" "}
						{announcement.endTime ? AnnouncementFormat.Fields.endTime(announcement.endTime) : "Does not expire"}
					</span>
				</If>
				<If condition={withAudience}>
					<SharingView announcementID={announcement.id} size="small" />
				</If>
				<If condition={withStatus}>
					<AnnouncementStatusBadge announcement={announcement} style={{ width: "fit-content", margin: "0 auto" }} />
				</If>
				<If condition={editable && !isPreview}>
					<AnnouncementUpdatePermissionRequired isAuthor={announcement.author?.isCurrent ?? false}>
						<ControlledAnnouncementUpdateModalController announcementID={announcement.id} variant="link" size="small">
							Edit
						</ControlledAnnouncementUpdateModalController>
					</AnnouncementUpdatePermissionRequired>
				</If>
			</AuthorTimestampView>

			<ModelComponents.ExpandableText lines={8} style={{ marginBottom: "1.5rem" }}>
				{announcement.body}
			</ModelComponents.ExpandableText>

			<If condition={withReactions}>
				<SocialReactionComponents.ReactionsView
					announcementID={announcement.id}
					reactions={socialData?.announcement.socialReactions ?? []}
					style={{ marginBottom: "1.5rem" }}
				/>
			</If>

			<AttachmentsView announcement={announcement} />
		</Column>
	);
}

export type AuthorTimestampViewProps = {
	announcement: Pick<DetailDisplayAnnouncement, "startTime" | "author">;
	isNew?: boolean;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function AuthorTimestampView({
	announcement,
	isNew = false,
	className,
	style,
	children,
}: AuthorTimestampViewProps) {
	const initialIsNew = React.useRef(isNew);

	const startTime = new Date(announcement.startTime);

	return (
		<Column
			justify="spaced-start"
			align="center"
			verticalSpacing="1rem"
			className={makeClassName(styles["detail-display__author-timestamp"], className)}
			style={style}
		>
			{announcement.author && <UserComponents.UserIcon user={announcement.author} size={100} />}

			<Column justify="spaced-start" align="center" verticalSpacing="0.25rem">
				<Row justify="spaced-center" align="center" horizontalSpacing="0.5rem">
					{announcement.author && (
						<UserLink
							instance={announcement.author}
							className={styles["detail-display__author-timestamp__author"] + " link--no-text-decoration"}
						>
							{UserFormat.name(announcement.author)}
						</UserLink>
					)}
					<If condition={initialIsNew.current}>
						<NewBadge className={styles["detail-display__author-timestamp__new-badge"]} />
					</If>
				</Row>
				<Timestamp value={announcement.startTime} className={styles["detail-display__author-timestamp__timestamp"]}>
					{startTime.toLocaleString(undefined, {
						year: startTime.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
						month: "long",
						weekday: "long",
						day: "numeric",
						hour: "numeric",
						minute: "2-digit",
					})}
				</Timestamp>
				{children}
			</Column>
		</Column>
	);
}

type AttachmentsViewProps = {
	announcement: DetailDisplayAnnouncement;
};

function AttachmentsView({ announcement }: AttachmentsViewProps) {
	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(announcement.mediaAttachments, announcement.body),
		[announcement.mediaAttachments, announcement.body],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	if (visualAttachments.length === 0 && fileAttachments.length === 0 && linkAttachments.length === 0) {
		return null;
	}

	return (
		<Section>
			<Section.Header>
				<Heading.H4 noMargin>Attachments</Heading.H4>
			</Section.Header>
			<Section.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<If condition={visualAttachments.length > 0}>
						<MediaAttachmentComponents.VisualAttachmentsRow
							attachments={visualAttachments}
							onClickAttachment={onClickMediaAttachment}
						/>

						<Modal.If condition={isMediaAttachmentModalOpen}>
							<MediaAttachmentComponents.MediaAttachmentModal
								mediaAttachments={visualAttachments}
								initialIndex={mediaAttachmentModalIndex}
								backButtonText="Back to Announcement"
								onClose={closeMediaAttachmentModal}
							/>
						</Modal.If>
					</If>
					<If condition={fileAttachments.length > 0}>
						<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
					</If>
					<If condition={linkAttachments.length > 0}>
						<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
					</If>
				</Column>
			</Section.Body>
		</Section>
	);
}
